import {CaseResult} from "src/app/model/lighthouse/ManagementModels";

/**
 * Column configuration for the Case Audit "Case Activity" table.
 * Columns:
 *   [Select] · Case ID · [Login] · DP ID · Reference Type · Case Type · Case Sub Type ·
 *   Creation Date · Enforcement Type · Country · Audit Type · Case Status · Take Action
 *   (Select is Manager-only, Login is QA/Manager-only; no Assignee; Dwell/Handle Time removed).
 *
 * NOTE: The Login column reads `latestAuditorAlias` — the auditor who most recently
 * submitted a decision (null for undecided/PENDING cases, so the cell renders empty).
 * Enforcement Type is not a separate response field: it equals caseSubType, so the column
 * reads caseSubType.
 */

const EMPTY = '-';

function formatDate(epochMs?: number): string {
    if (!epochMs && epochMs !== 0) return EMPTY;
    const d = new Date(epochMs);
    return isNaN(d.getTime()) ? EMPTY : d.toISOString().slice(0, 10);
}

function formatAuditType(auditType?: string): string {
    switch (auditType) {
        case 'PRIMARY_AUDIT': return 'Primary';
        case 'SECONDARY_AUDIT': return 'Secondary';
        case 'REOPEN_AUDIT': return 'Reopen';
        default: return auditType || EMPTY;
    }
}

export const ACTION_LABELS: { [action: string]: string } = {
    VIEW: 'View',
    REOPEN: 'Re-Open',
    RESUME: 'Resume'
};

function textCell(value: string) {
    const el = document.createElement('span');
    el.textContent = value;
    return {element: el};
}

export interface RowSelection {
    isSelected: (caseId: string) => boolean;
    onToggle: (caseId: string) => void;
    allSelected: boolean;            // true when every row on the current page is selected
    onToggleAll: () => void;         // select / clear all rows on the current page
}

/**
 * Builds the Case Activity columns.
 * @param onAction invoked with (action, caseId) when a Take Action button is clicked.
 * @param selection when provided (Manager only), prepends a left-hand checkbox column for row selection.
 * @param showLogin when true (QA/Manager), inserts the auditor Login column after Case ID.
 */
export function buildCaseActivityTableHeader(onAction: (action: string, caseId: string) => void,
                                             selection?: RowSelection,
                                             showLogin: boolean = false) {
    const buildSelectColumn = (sel: RowSelection) => ({
        label: '',
        headerFormatter: () => {
            const cb = document.createElement('kat-checkbox');
            if (sel.allSelected) cb.setAttribute('checked', 'true');
            cb.setAttribute('data-testid', 'row-select-all');
            cb.setAttribute('aria-label', 'Select all rows on this page');
            cb.addEventListener('change', () => sel.onToggleAll());
            return {element: cb};
        },
        formatter: (_i: number, r: CaseResult) => {
            const cb = document.createElement('kat-checkbox');
            if (sel.isSelected(r.caseId)) cb.setAttribute('checked', 'true');
            cb.setAttribute('data-testid', `row-select-${r.caseId}`);
            cb.setAttribute('aria-label', `Select case ${r.caseId}`);
            cb.addEventListener('change', () => sel.onToggle(r.caseId));
            return {element: cb};
        }
    });
    const columns: any[] = [
        {label: 'Case ID', property: 'caseId', sortable: true, sortValue: (r: CaseResult) => r.caseId},
        {label: 'DP ID', property: 'referenceId', sortable: true, sortValue: (r: CaseResult) => r.referenceId},
        {label: 'Reference Type', property: 'referenceSubType', sortable: true, sortValue: (r: CaseResult) => r.referenceSubType},
        {label: 'Case Type', property: 'caseType', sortable: true, sortValue: (r: CaseResult) => r.caseType},
        {label: 'Case Sub Type', property: 'caseSubType', sortable: true, sortValue: (r: CaseResult) => r.caseSubType},
        {
            label: 'Creation Date', sortable: true, sortValue: (r: CaseResult) => r.creationDate,
            formatter: (_i: number, r: CaseResult) => textCell(formatDate(r.creationDate))
        },
        // Enforcement type == caseSubType (the response has no separate field).
        {label: 'Enforcement Type', property: 'caseSubType', sortable: true, sortValue: (r: CaseResult) => r.caseSubType},
        {label: 'Country', property: 'country', sortable: true, sortValue: (r: CaseResult) => r.country},
        {
            label: 'Audit Type', sortable: true, sortValue: (r: CaseResult) => r.auditType,
            formatter: (_i: number, r: CaseResult) => textCell(formatAuditType(r.auditType))
        },
        {label: 'Case Status', property: 'status', sortable: true, sortValue: (r: CaseResult) => r.status},
        {
            label: 'Take Action',
            formatter: (_i: number, r: CaseResult) => {
                const container = document.createElement('div');
                container.className = 'case-action-cell';
                (r.availableActions || []).forEach(a => {
                    const btn = document.createElement('kat-button');
                    btn.setAttribute('label', ACTION_LABELS[a.action] || a.action);
                    btn.setAttribute('size', 'small');
                    btn.setAttribute('variant', 'secondary');
                    btn.setAttribute('data-action', a.action);
                    if (a.enabled === false) btn.setAttribute('disabled', 'true');
                    btn.addEventListener('click', () => onAction(a.action, r.caseId));
                    container.appendChild(btn);
                });
                return {element: container};
            }
        }
    ];
    if (showLogin) {
        // Auditor login — shown to QA/Manager (who see other auditors' cases), after Case ID.
        columns.splice(1, 0, {
            label: 'Login', property: 'latestAuditorAlias', sortable: true,
            sortValue: (r: CaseResult) => r.latestAuditorAlias
        });
    }
    return selection ? [buildSelectColumn(selection), ...columns] : columns;
}

export const _ManagementTableFormatters = {formatDate, formatAuditType};
